import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { resources } from "@/i18n/translations";

const I18nContext = createContext(null);
const STORAGE_KEY = "dairyops_lang";

function getByPath(obj, path) {
  return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

function interpolate(str, vars) {
  if (!vars || typeof str !== "string") return str;
  return Object.keys(vars).reduce(
    (acc, k) => acc.replace(new RegExp(`\\{${k}\\}`, "g"), vars[k]),
    str,
  );
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || "ro"; } catch { return "ro"; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch (e) { console.warn("i18n: could not persist language preference", e); }
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l) => setLangState(l === "ro" ? "ro" : "en"), []);

  // t("namespace.key", { vars }) — falls back to English, then to the raw key
  const t = useCallback((key, vars) => {
    let val = getByPath(resources[lang], key);
    if (val == null) val = getByPath(resources.en, key);
    if (val == null) return key;
    return interpolate(val, vars);
  }, [lang]);

  // td(value) — translate a built-in data value (product type / default status); else pass through
  const td = useCallback((value) => {
    if (value == null) return value;
    const map = (resources[lang] && resources[lang].data) || {};
    return map[value] != null ? map[value] : value;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t, td }), [lang, setLang, t, td]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Safe fallback if provider is missing (prevents crashes)
    return { lang: "en", setLang: () => {}, t: (k) => k, td: (v) => v };
  }
  return ctx;
}
